import React, { useState } from 'react';
import accounting from 'accounting';
import { createUseStyles } from 'react-jss';
import { ChevronIcon } from '@spoiler-alert/ui-library';
import { weightWithUnit } from '../../../utilities/unit-helper';
import { laneTruckMultiplier, laneTruckCountDisplay } from '../../trucklane/trucklane-utility';
import { layoutStyles } from './offer-card-styles';
import Tracking from '../../../utilities/tracking-apis';
import { OfferCardActiveTrucklane, OfferCardUser } from './offer-card-types';
import { Inventory, OfferListing, TruckTypeAmounts } from '../../../graphql/query-result-types';

const useStyles = createUseStyles(layoutStyles, { name: 'OfferCard' });

interface TrucklaneDetailsProps {
  activeTruckLane: OfferCardActiveTrucklane;
  inventory: Inventory;
  offerInfo: OfferListing;
  isAwarded: boolean;
  quantity: number;
  user: OfferCardUser;
  unitPrice: number;
}

const TrucklaneDetails: React.FC<TrucklaneDetailsProps> = ({ activeTruckLane, offerInfo, inventory, isAwarded, quantity, user, unitPrice }) => {
  const classes = useStyles();
  const [chevronDirection, setChevronDirection] = useState<'up' | 'down' | 'left' | 'right'>('down');

  const offerAwarded = offerInfo.status === 'AWARDED';
  const summedRevenue = (activeTruckLane?.totalRevenue || 0) + (offerAwarded ? 0 : quantity * (unitPrice * (offerInfo.pricingDivisor || 1)));
  const summedWeight = (activeTruckLane?.totalWeight || 0) + (offerAwarded ? 0 : quantity * inventory.unitGrossWeight);
  const pricePerCase = unitPrice * (offerInfo.pricingDivisor || 1);

  const onTrucklaneClick = () => {
    if (chevronDirection === 'down') {
      Tracking.Event('OC - Trucklane Info Expanded', {
        userId: user._id,
        offerId: offerInfo._id,
      });
      setChevronDirection('up');
    }
    if (chevronDirection === 'up') setChevronDirection('down');
  };

  // A casesPerPallet of 0 or null would make every pallet division Infinity and carry that into truck count,
  // lane cost and net revenue; an undefined one would make it NaN and slip past the load > 0 guards. Fall back
  // to one case per pallet, matching how trucklane-utility guards the same division.
  const casesPerPallet = inventory.casesPerPallet || 1;

  const getTotalPallets = () => {
    if (isAwarded) return activeTruckLane?.totalPallets;
    return (activeTruckLane?.totalPallets || 0) + quantity / casesPerPallet;
  };

  const getLaneCostCell = (value: number) => {
    if (value > 0) {
      return <span className={classes.trucklaneCellNegative}>({accounting.formatMoney(value)})</span>;
    }
    return <span className={classes.trucklaneCell}>{accounting.formatMoney(value)}</span>;
  };

  const getNetRevenueCell = (value: number) => {
    if (value < 0) {
      return <span className={classes.trucklaneCellNegative}>({accounting.formatMoney(-1 * value)})</span>;
    }
    return <span className={classes.trucklaneCell}>{accounting.formatMoney(value)}</span>;
  };

  const truckMaximumsSet = user?.site?.truckMaximums?.mode !== 'none' && (user?.site?.truckMaximums?.weight || user?.site?.truckMaximums?.pallets);

  // Truck count per column = each column's load (in the site's max unit) divided by this lane's truck-type max.
  const maxMode = user?.site?.truckMaximums?.mode;
  const maxBucket = maxMode === 'weight' || maxMode === 'pallets' ? user?.site?.truckMaximums?.[maxMode] : undefined;
  // Truck type lives on the inventory (offerInfo omits it from the offer-listings query selection).
  const laneMax = maxBucket?.[inventory.truckType?.toLowerCase() as keyof TruckTypeAmounts];
  const truckCountLoads =
    maxMode === 'pallets'
      ? { current: activeTruckLane?.totalPallets || 0, thisOffer: quantity / casesPerPallet, total: getTotalPallets() || 0 }
      : { current: activeTruckLane?.totalWeight || 0, thisOffer: inventory.unitGrossWeight * quantity, total: summedWeight };

  // Shown to one decimal; a dash when this truck type has no max configured.
  const getTruckCountCell = (load: number) => (
    <span className={classes.trucklaneCell}>{laneMax ? accounting.formatNumber(laneTruckCountDisplay(laneMax, load), 1) : '—'}</span>
  );

  // A partial truck still incurs a full delivery cost, so lane cost scales by whole trucks: 1.2 trucks of load
  // costs two, 2.1 costs three. Attribution is unchanged from the single-truck behaviour — the active lane
  // carries the trucks its own load fills, and this offer carries only the trucks it adds on top. With no
  // maximum configured every multiplier is 1, which reproduces the previous flat cost exactly.
  const perTruckCost = offerInfo.trucklaneCost || 0;
  const currentLaneCost = activeTruckLane ? perTruckCost * laneTruckMultiplier(laneMax, truckCountLoads.current) : 0;
  const totalLaneCost = perTruckCost * laneTruckMultiplier(laneMax, truckCountLoads.total);
  const thisOfferLaneCost = totalLaneCost - currentLaneCost;

  const renderExpandedTrucklane = () => (
    <div className={classes.expandedTrucklaneContainer}>
      <div className={classes.firstTrucklaneRow}>
        <span className={classes.trucklaneCell} />
        <span className={classes.header}>CURRENTLY</span>
        <span className={classes.header}>THIS OFFER</span>
        <span className={classes.header}>TOTAL</span>
      </div>
      <div className={classes.trucklaneRowWithBorder}>
        <span className={classes.header}>WEIGHT</span>
        <span className={classes.trucklaneCell}>{weightWithUnit(activeTruckLane?.totalWeight, user, true)}</span>
        <span className={classes.trucklaneCell}>{weightWithUnit(inventory.unitGrossWeight * quantity, user, true)}</span>
        <span className={classes.trucklaneCell}>{weightWithUnit(summedWeight, user, true)}</span>
      </div>
      <div className={classes.trucklaneRowWithBorder}>
        <span className={classes.header}>PALLETS</span>
        <span className={classes.trucklaneCell}>{accounting.formatNumber(activeTruckLane?.totalPallets)}</span>
        <span className={classes.trucklaneCell}>{accounting.formatNumber(quantity / casesPerPallet)}</span>
        <span className={classes.trucklaneCell}>{accounting.formatNumber(getTotalPallets())}</span>
      </div>
      {truckMaximumsSet && (
        <div className={classes.trucklaneRowWithBorder}>
          <span className={classes.header}>TRUCK COUNT</span>
          {getTruckCountCell(truckCountLoads.current)}
          {getTruckCountCell(truckCountLoads.thisOffer)}
          {getTruckCountCell(truckCountLoads.total)}
        </div>
      )}
      <div className={classes.trucklaneRowWithBorder}>
        <span className={classes.header}>REVENUE</span>
        <span className={classes.trucklaneCell}>{accounting.formatMoney(activeTruckLane?.totalRevenue)}</span>
        <span className={classes.trucklaneCell}>{accounting.formatMoney(quantity * pricePerCase)}</span>
        <span className={classes.trucklaneCell}>{accounting.formatMoney(summedRevenue)}</span>
      </div>
      <div className={classes.trucklaneRowWithBorder}>
        <span className={classes.header}>LANE COST</span>
        {getLaneCostCell(currentLaneCost)}
        {getLaneCostCell(thisOfferLaneCost)}
        {getLaneCostCell(totalLaneCost)}
      </div>
      <div className={classes.trucklaneRowNetRevenue}>
        <span className={classes.headerNetRevenue}>NET REVENUE</span>
        {getNetRevenueCell(activeTruckLane?.totalRevenue - currentLaneCost)}
        {getNetRevenueCell(quantity * pricePerCase - thisOfferLaneCost)}
        {getNetRevenueCell(summedRevenue - totalLaneCost)}
      </div>
    </div>
  );

  return (
    <>
      <div className={activeTruckLane ? classes.trucklane : classes.inactiveTrucklane} onClick={onTrucklaneClick} data-testid="trucklane-info">
        <span className={classes.trucklaneText}>{`${offerInfo.logisticsTerm?.toUpperCase()} TRUCK LANE`}</span>
        <div className={classes.statusContainer}>
          {offerInfo.trucklaneDistance && (
            <span className={classes.trucklaneDistance} id="trucklaneDistance">
              {accounting.formatNumber(offerInfo.trucklaneDistance)} mi.
            </span>
          )}
          <span className={activeTruckLane ? classes.statusText : classes.inactiveStatusText} id="StatusText">
            {activeTruckLane ? 'ACTIVE' : 'INACTIVE'}
          </span>
          <span className={activeTruckLane ? classes.chevronContainer : classes.inactiveChevronContainer} id="ChevronContainer">
            <ChevronIcon className={classes.chevron} direction={chevronDirection} id="Chevron" />
          </span>
        </div>
      </div>
      {chevronDirection === 'up' && renderExpandedTrucklane()}
    </>
  );
};

export default TrucklaneDetails;
